import { formatDate, formatDay, formatDayYear } from '../../lib/format/dates.ts';
import { tenureNotes, type TenureNote } from '../../lib/scheduling/people.ts';
import type { DistributionRow, Physician } from '../api.ts';
import { copy } from '../copy/en-CA.ts';
import { ScrollRegion } from './ScrollRegion.tsx';

const c = copy.distribution;

/** "joined Mon 31 Aug", "last day Mon 31 Aug 2026": the year only when the date is outside the schedule. */
function noteText(note: TenureNote): string {
  const day = note.withYear ? formatDayYear(note.date) : formatDay(note.date);
  return note.kind === 'joined' ? c.joined(day) : c.lastDay(day);
}

export function DistributionTable({
  rows,
  physicians,
  schedule,
  myPhysicianId,
  showLastSpecial,
}: {
  rows: DistributionRow[];
  physicians: Physician[];
  /** The schedule's own dates, against which a start or last day inside it is noted. */
  schedule: { startDate: string; endDate: string };
  myPhysicianId: string | null;
  showLastSpecial: boolean;
}) {
  const byId = new Map(physicians.map((p) => [p.id, p]));
  const max = Math.max(1, ...rows.map((r) => r.weeks));
  return (
    <div className="card" style={{ padding: 0 }}>
      <ScrollRegion label={copy.schedule.tabs.distribution} style={{ border: 'none' }}>
        <table className="distribution">
          <thead>
            <tr>
              <th>{c.columns.physician}</th>
              <th>{c.columns.weeks}</th>
              <th>{c.columns.longWeekends}</th>
              <th>{c.columns.special}</th>
              <th>{c.columns.leave}</th>
              <th className="muted">{c.columns.lw12}</th>
              {showLastSpecial ? <th className="muted">{c.columns.lastSpecial}</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const p = byId.get(r.physicianId);
              const mine = r.physicianId === myPhysicianId;
              // Someone who joined, or whose last day came, during this
              // schedule has fewer weeks for that reason; say so beside the name.
              const notes = p ? tenureNotes(p, schedule) : [];
              return (
                <tr key={r.physicianId} className={mine ? 'mine' : ''}>
                  <td>
                    <span className="physician">
                      <span className="dot" style={{ background: p?.colour ?? '#888' }} aria-hidden="true" />
                      <span className="physician-name">{r.name}</span>
                    </span>
                    {mine ? <span className="badge mine">{copy.schedule.you}</span> : null}
                    {notes.map((n) => (
                      <span key={n.kind} className="tenure-note muted small">
                        {noteText(n)}
                      </span>
                    ))}
                  </td>
                  <td>
                    <span className="stat">{r.weeks}</span>
                    <span className="bartrack" aria-hidden="true">
                      <span className="bar" style={{ width: `${String((r.weeks / max) * 100)}%` }} />
                    </span>
                  </td>
                  <td>
                    <span className="stat">{r.longWeekends}</span>
                  </td>
                  <td>
                    <span className="stat">{r.specialWeeks}</span>
                  </td>
                  <td className="muted">{r.leaveWeeks || '–'}</td>
                  <td className="muted">{r.twelveMonthLongWeekends}</td>
                  {showLastSpecial ? <td className="muted">{r.lastSpecialWeek ? formatDate(r.lastSpecialWeek) : c.never}</td> : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </ScrollRegion>
      <p className="muted small" style={{ padding: '0.8rem 1rem', borderTop: '1px solid var(--border)' }}>
        {c.note}
      </p>
    </div>
  );
}
